/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadTransitLCRReport } from "../../../../utils/downloadUtils";
import { useNavigate } from "react-router-dom";
import API from "../../../../api";
import urls from "../../../../api/urls";
import { showErrorMessage } from "../../../../utils";

const TransitLCRReport = () => {
    const FILTER_STORAGE_KEY = "LCR_TALLY_REPORT_FILTERS";

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(2, "days"),
        moment().subtract(1, "days")
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedDestinationHub, setSelectedDestinationHub] = useState([]);
    const [statusList] = useState([
        { value: "UNLOCKED", label: "UNLOCKED" },
        { value: "LOCKED", label: "LOCKED" },

        // Add more LCR-specific statuses as needed
    ]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [stopLcrNums, setStopLcrNums] = useState("");
    const [vehicleLcrNums, setVehicleLcrNums] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const navigate = useNavigate();
    const calendarRef = useRef();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);

    // Helper function to save filters to sessionStorage
    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange ? [
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            ] : null,
            selectedHubs,
            selectedDestinationHub,
            selectedStatus,
            stopLcrNums,
            vehicleLcrNums,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    // Helper function to restore filters from sessionStorage
    const restoreFiltersFromStorage = () => {
        try {
            const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                    setDateRange([
                        moment(filters.dateRange[0]),
                        moment(filters.dateRange[1])
                    ]);
                }
                if (filters.selectedHubs && filters.selectedHubs.length > 0) {
                    setSelectedHubs(filters.selectedHubs);
                }
                if (filters.selectedDestinationHub && filters.selectedDestinationHub.length > 0) {
                    setSelectedDestinationHub(filters.selectedDestinationHub);
                }
                if (filters.selectedStatus && filters.selectedStatus.length > 0) {
                    setSelectedStatus(filters.selectedStatus);
                }
                if (filters.stopLcrNums) {
                    setStopLcrNums(filters.stopLcrNums);
                }
                if (filters.vehicleLcrNums) {
                    setVehicleLcrNums(filters.vehicleLcrNums);
                }
                if (filters.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        current: filters.pagination.current || prev.current,
                        pageSize: filters.pagination.pageSize || prev.pageSize
                    }));
                }
                // Clear the saved filters after restoring
                sessionStorage.removeItem(FILTER_STORAGE_KEY);
                return true;
            }
        } catch (error) {
            console.error("Error restoring filters:", error);
        }
        return false;
    };

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
                // Restore filters after hub list is loaded
                restoreFiltersFromStorage();
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line
    }, []);

    // Set default hub for HUBADMIN users (only if no hubs are already selected and no filters were restored)
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        let userid = JSON.parse(localStorage.getItem("userId") || "{}");
        setHubUser(user?.roles?.[0]);
        // Check if there are saved filters - if yes, don't set default hub (restore will handle it)
        // Check if there are saved filters - if yes, don't set default hub (restore will handle it)
        const hasSavedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
        const activeHub = JSON.parse(localStorage.getItem("active_hub"));
        const activeHubId = activeHub?.hubId;

        // Only set default hub if no hubs are selected and no filters were saved (to avoid overriding restored filters)
        if (hubList.length > 0 && selectedHubs.length === 0 && !hasSavedFilters) {
            if (activeHubId) {
                const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
                if (defaultHub) {
                    setSelectedHubs([defaultHub.value]);
                }
            } else if (user?.roles?.[0] === "ROLE_HUBADMIN") {
                const defaultHub = hubList.find(hub => String(hub.value) === String(userid?.userInfo?.premiseId));
                if (defaultHub) {
                    setSelectedHubs([defaultHub.value]);
                }
            }
        }
    }, [hubList]);

    // Fetch report data when filters or pagination change
    useEffect(() => {
        // Wait for hubUser to be initialized
        if (!hubUser) {
            return;
        }
        // Only restrict for HUBADMIN with no hub selected
        if (hubUser === "ROLE_HUBADMIN" && selectedHubs.length === 0) {
            return;
        }
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, selectedHubs, selectedDestinationHub, selectedStatus, stopLcrNums, vehicleLcrNums, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            setReportData([]);
            const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
            const selectedDestHubData = hubList.filter(hub => selectedDestinationHub?.includes(hub.value));

            const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
            const params = {
                ...(selectedHubData?.length > 0 && { originHubId: selectedHubData.map(hub => hub.value).join(",") }),
                ...(selectedDestHubData?.length > 0 && { destinationHubId: selectedDestHubData.map(hub => hub.value).join(",") }),
                ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { tripStatus: selectedStatus.join(",") }),
                ...(stopLcrNums && { stopLcrNums: stopLcrNums }),
                ...(vehicleLcrNums && { vehicleLcrNums: vehicleLcrNums }),
                page: (pagination.current - 1), // Convert to 0-based
                size: pagination.pageSize
            };

            console.log("Incoming LCR Report params:", params);

            // Call the Incoming LCR report API

            const { data, total, count, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getLCRTallyLCRReport(params);
            setLastsynced(lastRefreshedAt);
            setNextScheduledRefresh(nextScheduledRefresh);
            setReportData(data);
            setPagination(prev => ({
                ...prev,
                total: typeof total === "number" ? total : (typeof count === "number" ? count : (Array.isArray(data) ? data.length : 0)),
            }));
        } catch {
            message.error("Failed to fetch report data");
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
        if (!hasValidDates) {
            message.error("Please select a date range for download");
            return;
        }
        setDownloadLoading(true);
        try {
            const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
            const selectedDestHubData = hubList.filter(hub => selectedDestinationHub?.includes(hub.value));

            const downloadParams = {
                ...(selectedHubData?.length > 0 && { originHubId: selectedHubData.map(hub => hub.value).join(",") }),
                ...(selectedDestHubData?.length > 0 && { destinationHubId: selectedDestHubData.map(hub => hub.value).join(",") }),
                ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { tripStatus: selectedStatus.join(",") }),
                ...(stopLcrNums && { stopLcrNums: stopLcrNums }),
                ...(vehicleLcrNums && { vehicleLcrNums: vehicleLcrNums })
            };

            await downloadTransitLCRReport(downloadParams);
            message.success("Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleDateRangeChange = (value) => {
        if (!value || !value[0] || !value[1]) {
            // If cleared, reset to today and open calendar
            setDateRange([moment(), moment()]);
            // Open the calendar dropdown
            if (calendarRef.current && calendarRef.current.picker) {
                calendarRef.current.picker.open();
            }
        } else {
            setDateRange(value);
        }
    };

    // Handler for cell clicks (fetch details and navigate)
    const handleCellClick = async (type, record) => {
        // Save current filters before navigating
        saveFiltersToStorage();

        let status = type;
        // Map shipment types to required API status
        if (type === "shipmentOut") status = "SHIPMENT_OUT";
        else if (type === "shipmentIn") status = "SHIPMENT_IN";
        else if (type === "shipmentPending") status = "SHIPMENT_PENDING";
        // Map bag types to required API status
        else if (type === "bagOut") status = "BAG_OUT";
        else if (type === "bagIn") status = "BAG_IN";
        else if (type === "bagPending") status = "BAG_PENDING";
        let detailType = ["bagOut", "bagIn", "bagPending"].includes(type) ? "Bag" : "Shipment";
        let endpoint = detailType === "Bag" ? urls.BAG_DETAIL_VIEW_LCR : urls.SHIPMENT_DETAIL_VIEW_LCR;
        let lcrNum = record.stopLcrNum;
        let vehicleNum = record.vehicleNum || record.vehicle_num;
        let currentHub = record.originHub || record.fromHub || record.current_hub;
        try {
            const queryParams = {
                lcrNum,
                type: status,
            };
            const apiResult = await API.Api(endpoint).getBagDetailsParams(queryParams);
            if (apiResult.response.data.code == 200) {
                let dataArr = apiResult.response.data.data;
                if (apiResult.response.data.data.length > 0) {
                    navigate("/detailed-inout-view", {
                        state: {
                            vehicle_lcr_id: lcrNum,
                            vehicle_num: vehicleNum,
                            current_hub: currentHub,
                            type: detailType,
                            status,
                            data: dataArr,
                            pageFrom: "LCRTallyReport",
                            url: "/transit-lcr"
                        },
                    });
                } else {
                    showErrorMessage(apiResult.response.data.message);
                }
            } else {
                showErrorMessage("Something went wrong");
            }

            //console.log("apiResult:", JSON.stringify(apiResult.response.data.message, null, 2));

        } catch (error) {
            message.error("An error occurred while fetching details");
        }
    };

    return (
        <div className="report-container transit-lcr-report">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="LCR Tally" navigateTo="/reports">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From & To Date</div>
                        <CalendarComponent
                            ref={calendarRef}
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Destination Hub"
                            options={hubList}
                            onChange={setSelectedDestinationHub}
                            value={selectedDestinationHub}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">To Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Origin Hub"
                            options={hubList}
                            onChange={setSelectedHubs}
                            value={selectedHubs}
                            allowClear
                            mode={hubUser !== "ROLE_HUBADMIN" ? "multiple" : "default"}
                            disabled={hubUser === "ROLE_HUBADMIN"}
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    {<Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Trip Status</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Trip Status"
                            options={statusList}
                            onChange={setSelectedStatus}
                            value={selectedStatus}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </Col>}

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Trip ID</div>
                        <Input
                            placeholder="Enter comma separated Trip ID"
                            value={vehicleLcrNums}
                            onChange={(e) => setVehicleLcrNums(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Stop ID</div>
                        <Input
                            placeholder="Enter comma separated Stop ID"
                            value={stopLcrNums}
                            onChange={(e) => setStopLcrNums(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} style={{ textAlign: "right", paddingTop: "24px" }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
                <Row style={{ marginTop: 16 }}>

                </Row>
            </Card>
            {/* New Table for Provided Data */}
            <TableComponent
                columns={[
                    {
                        title: "Trip ID", dataIndex: "vehicleLcrNum", key: "vehicleLcrNum", sorter: true, align: "center",
                        render: (text, record) => (
                            <span
                                style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => {
                                    const recordToStore = {
                                        ...record,
                                        vehicle_lcr_id: record.vehicleLcrNum
                                    };
                                    sessionStorage.setItem("LCR_REPORT_RECORD", JSON.stringify(recordToStore));
                                    window.open(window.location.origin + "/lcr-report", "_blank");
                                }}
                            >
                                {text}
                            </span>
                        )
                    },
                    {
                        title: "Stop ID", dataIndex: "stopLcrNum", key: "stopLcrNum", sorter: true,
                        render: (text, record) => (
                            <span
                                style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => {
                                    const recordToStore = {
                                        ...record,
                                        vehicle_lcr_id: record.stopLcrNum
                                    };
                                    sessionStorage.setItem("LCR_REPORT_RECORD", JSON.stringify(recordToStore));
                                    window.open(window.location.origin + "/lcr-report", "_blank");
                                }}
                            >
                                {text}
                            </span>
                        )
                    },
                    { title: "Unlock Duration", dataIndex: "lcrUnlockDuration", key: "lcrUnlockDuration", sorter: true, align: "center" },
                    { title: "Trip Status", dataIndex: "tripStatus", key: "tripStatus", sorter: true, align: "center" },
                    { title: "Generated At", dataIndex: "tripCreatedTime", key: "tripCreatedTime", sorter: true, align: "center" },
                    { title: "Departed At", dataIndex: "tripDepartedTime", key: "tripDepartedTime", sorter: true, align: "center" },
                    { title: "Received At", dataIndex: "lcrReceiveTime", key: "lcrReceiveTime", sorter: true, align: "center" },
                    { title: "Completed At", dataIndex: "lcrCompleteTime", key: "lcrCompleteTime", sorter: true, align: "center" },
                    { title: "LCR Status", dataIndex: "lcrStatus", key: "lcrStatus", sorter: true, align: "center" },
                    { title: "Last Activity", dataIndex: "lastLcrOperationTime", key: "lastLcrOperationTime", sorter: true, align: "center" },
                    { title: "From Hub", dataIndex: "originHub", key: "originHub", sorter: true },
                    { title: "To Hub", dataIndex: "destinationHub", key: "destinationHub", sorter: true },
                    { title: "Mode", dataIndex: "mode", key: "mode", sorter: true },
                    { title: "Flight No.", dataIndex: "flightNum", key: "flightNum", sorter: true, align: "center" },
                    { title: "Co Loader", dataIndex: "coLoader", key: "coLoader", sorter: true, align: "center" },
                    { title: "Vehicle No", dataIndex: "vehicleNum", key: "vehicleNum", sorter: true, align: "center" },
                    { title: "Driver", dataIndex: "driverName", key: "driverName", sorter: true, align: "center" },
                    { title: "Driver Phone No", dataIndex: "driverPhoneNo", key: "driverPhoneNo", sorter: true, align: "center" },
                    { title: "Bag Status", dataIndex: "bagStatus", key: "bagStatus", sorter: true, align: "center" },
                    {
                        title: "Bag Loaded", dataIndex: "loadedBagCount", key: "loadedBagCount", sorter: true, align: "center",
                        render: (text, record) => (
                            <> {(record.loadedBagCount == 0 || record.loadedBagCount == null) ? <span>{record.loadedBagCount ?? "---"}</span> : (
                                <span
                                    style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                    onClick={() => handleCellClick("bagOut", record)}
                                >
                                    {text}
                                </span>)}
                            </>
                        )
                    },
                    {
                        title: "Bags Received", dataIndex: "receivedBagCount", key: "receivedBagCount", sorter: true, align: "center",
                        render: (text, record) => (
                            <> {(record.receivedBagCount == 0 || record.receivedBagCount == null) ? <span>{record.receivedBagCount ?? "---"}</span> : (
                                <span
                                    style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                    onClick={() => handleCellClick("bagIn", record)}
                                >
                                    {text}
                                </span>)}
                            </>
                        )
                    },
                    {
                        title: "Bag Variance", dataIndex: "bagVariance", key: "bagVariance", sorter: true, align: "center", width: 20,
                        render: (text, record) => (
                            <> {(record.bagVariance == 0 || record.bagVariance == null) ? <span>{record.bagVariance ?? "---"}</span> : (
                                <span
                                    style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                    onClick={() => handleCellClick("bagPending", record)}
                                >
                                    {text}
                                </span>)}
                            </>
                        )
                    },
                    { title: "Individual Shipments Status", dataIndex: "shipmentStatus", key: "shipmentStatus", sorter: true, align: "center" },
                    {
                        title: "Individual Shipments Loaded", dataIndex: "loadedShipmentCount", key: "loadedShipmentCount", sorter: true, align: "center",
                        render: (text, record) => (
                            <> {(record.loadedShipmentCount == 0 || record.loadedShipmentCount == null) ? <span>{record.loadedShipmentCount ?? "---"}</span> : (
                                <span
                                    style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                    onClick={() => handleCellClick("shipmentOut", record)}
                                >
                                    {text}
                                </span>)}
                            </>
                        )
                    },
                    {
                        title: "Individual Shipments Received", dataIndex: "receivedShipmentCount", key: "receivedShipmentCount", sorter: true, align: "center",
                        render: (text, record) => (
                            <> {(record.receivedShipmentCount == 0 || record.receivedShipmentCount == null) ? <span>{record.receivedShipmentCount ?? "---"}</span> : (
                                <span
                                    style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                    onClick={() => handleCellClick("shipmentIn", record)}
                                >
                                    {text}
                                </span>)}
                            </>
                        )
                    },
                    {
                        title: "Individual Shipments Variance", dataIndex: "shipmentVariance", key: "shipmentVariance", sorter: true, align: "center", width: 20,
                        render: (text, record) => (
                            <> {(record.shipmentVariance == 0 || record.shipmentVariance == null) ? <span>{record.shipmentVariance ?? "---"}</span> : (
                                <span
                                    style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                    onClick={() => handleCellClick("shipmentPending", record)}
                                >
                                    {text}
                                </span>)}
                            </>
                        )
                    },
                    { title: "Total Weight (kg)", dataIndex: "totalWeight", key: "totalWeight", sorter: true, align: "center" },
                    { title: "Delay", dataIndex: "delay", key: "delay", sorter: true, align: "center" },
                    { title: "Is Anomaly", dataIndex: "isAnomaly", key: "isAnomaly", sorter: true, align: "center", },
                ]}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey="stopLcrNum"
                style={{ marginTop: 32 }}
            />
        </div>
    );
};

export default TransitLCRReport;
